import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// See /design-system/GUIDE.md.
// - `primary` (solid yellow) is the ONE main action per screen; everything
//   else is `secondary` / `ghost`.
// - Disabled is neutral gray, never pale yellow or opacity on yellow.
//   `disabled:` comes after `hover:`/`active:` in Tailwind's variant order,
//   so the disabled colors win over hover.
// - Radius: text buttons rounded-xl (12px), icon buttons rounded-lg (8px).
const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface disabled:cursor-not-allowed [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				primary:
					"bg-action-primary text-text-on-brand shadow-sm hover:bg-action-primary-hover active:bg-action-primary-pressed disabled:bg-action-disabled-bg disabled:text-action-disabled-fg disabled:shadow-none",
				secondary:
					"border border-border-subtle bg-bg-surface text-text-primary hover:bg-bg-sunken disabled:bg-bg-surface disabled:text-action-disabled-fg",
				ghost:
					"text-text-secondary hover:bg-bg-sunken hover:text-text-primary disabled:bg-transparent disabled:text-action-disabled-fg",
				danger:
					"bg-danger text-bg-surface hover:bg-danger/90 disabled:bg-action-disabled-bg disabled:text-action-disabled-fg",
				outline:
					"border border-border-subtle bg-transparent text-text-primary hover:bg-bg-sunken disabled:bg-transparent disabled:text-action-disabled-fg",
			},
			size: {
				sm: "h-8 px-3 text-xs rounded-xl",
				md: "h-9 px-4 text-sm rounded-xl",
				lg: "h-10 px-5 text-sm rounded-xl",
				"icon-xs": "h-6 w-6 rounded-lg",
				"icon-sm": "h-7 w-7 rounded-lg",
				icon: "h-9 w-9 rounded-lg",
				"icon-lg": "h-10 w-10 rounded-xl",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "md",
		},
	},
);

interface ButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);

Button.displayName = "Button";

export { Button, buttonVariants, type ButtonProps };
